// @flow
import * as React from 'react';
import { withRouter } from 'react-router';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { range, isEmpty } from 'utils/functional';
import type { TileCarouselResult } from 'api/shared';
import { LogoImage } from 'components/Image';
import Flex from 'components/Flex';
import Box from 'components/Box';
import ArrowNavigation from 'components/ArrowNavigation';
import Button, { DotNavigationButton } from 'components/Button';
import SearchTile from 'components/SearchTile';
import {
  RichTextHeadline,
  H4,
  H5,
  BodySmall,
  BodyMicro
} from 'components/Text';
import Carousel, { Slide } from 'containers/Carousel';
import { RouterLink as Link } from 'components/Link';
import { SearchTileLink } from 'components/SearchHit';
import { trackClicked } from 'utils/analytics';
import { colors, breakpoints } from '../theme';

const TruncatedText = styled.span`
  text-overflow: ellipsis;
  /* stylelint-disable-next-line value-no-vendor-prefix */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -moz-line-clamp: 3;
  /* stylelint-disable-next-line property-no-vendor-prefix */
  -webkit-box-orient: vertical;
  overflow: hidden;
  @media (min-width: ${breakpoints[2]}) {
    min-height: 70px;
  }
`;

const Section = ({
  result,
  history,
  headline,
  listingType,
  carouselLocation,
  fromListingId,
  ...props
}: {
  result: TileCarouselResult,
  history: RouterHistory,
  headline: ?string,
  listingType: string,
  carouselLocation?: string,
  fromListingId?: string
}) => {
  let location = result.city || '';
  if (result.city && result.state) {
    location += ', ';
  }
  if (result.state) {
    location += result.state;
  }
  return (
    <Box m="4px auto" px="4px">
      <SearchTile
        component={Button}
        display="block"
        p="20px !important"
        width="100%"
        height={[310, null, 330]}
        {...props}
      >
        <SearchTileLink
          url={result.url}
          trackClick={() => {
            trackClicked('Featured Listing', {
              listing_type: listingType,
              from_listing_id: fromListingId || null,
              to_listing_id: result.objectID,
              carousel_location: carouselLocation || null
            });
          }}
          aria-label={result.name}
        />
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          flexDirection="column"
          width="100%"
          height="100%"
          textAlign="left"
          mb="10px"
        >
          <Box width="100%">
            <Box height={120} display="flex" alignItems="center">
              <LogoImage
                width="100%"
                height="100%"
                justifyContent="flex-start"
                fsWidth={240}
                fsHeight={120}
                alt={result.name}
                handle={result.logo ? result.logo.handle : undefined}
              />
            </Box>
            <H5
              width="100%"
              mt={20}
              mb="4px"
              color={colors.battleshipGrey}
              lineHeight={1.375}
            >
              <Box
                as="span"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                width="100%"
                display="inline-block"
              >
                {result.orgName}
              </Box>
            </H5>
            <Box maxHeight={86} overflow="hidden">
              <H4 my={0} lineHeight={1.375}>
                <TruncatedText>{result.name}</TruncatedText>
              </H4>
            </Box>
          </Box>
          <BodyMicro color={colors.battleshipGrey}>{location}</BodyMicro>
        </Box>
      </SearchTile>
    </Box>
  );
};

const TileCarousel = ({
  headline,
  results,
  history,
  seeAllText,
  seeAllUrl,
  listingType,
  carouselLocation,
  fromListingId
}: {
  headline: ?string,
  results: Array<TileCarouselResult>,
  history: RouterHistory,
  seeAllText?: string,
  seeAllUrl?: string,
  listingType: string,
  carouselLocation?: string,
  fromListingId?: string
}) => (
  <>
    <Box>
      {headline && (
        <RichTextHeadline textAlign="center">{headline}</RichTextHeadline>
      )}
      {seeAllText && seeAllUrl && (
        <BodySmall textAlign={['center', null, null, 'right']} mb={20}>
          <Link data-qa-id="tile-carousel-see-all" to={seeAllUrl}>
            {seeAllText}
          </Link>
        </BodySmall>
      )}
    </Box>
    <Box>
      <Box
        maxWidth={[250, null, 718, 'none']}
        mx={['auto', null, null, -24]}
        width="auto"
        px={[12, null, null, 20]}
      >
        <Carousel
          data-qa-id="tile-carousel"
          pageLength={[1, 1, 3, 4, 4]}
          navigation={[
            ({
              current,
              total,
              goToPage,
              prevPage,
              nextPage
            }: {
              current: number,
              total: number,
              goToPage: Function,
              prevPage: Function,
              nextPage: Function
            }) => (
              <nav>
                <>
                  <ArrowNavigation
                    nextPage={nextPage}
                    prevPage={prevPage}
                    current={current}
                    total={total}
                    outset
                  />
                  {total > 1 && (
                    <Box position="relative" height={32}>
                      <Flex
                        position="absolute"
                        bottom={0}
                        left="50%"
                        transform="translateX(-50%)"
                        justifyContent="center"
                        maxWidth="calc(100vw - 16px)"
                      >
                        {range(total).map(index => (
                          <DotNavigationButton
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            goToPage={goToPage}
                            index={index}
                            isActive={index === current}
                          />
                        ))}
                      </Flex>
                    </Box>
                  )}
                </>
              </nav>
            )
          ]}
          slides={() =>
            !isEmpty(results)
              ? results.map((result, index) => (
                  <Slide
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${'section'}-${index}`}
                    data-qa-id="tile-carousel-slide"
                    data-qa-listing-id={result.objectID}
                  >
                    <Section
                      history={history}
                      result={result}
                      listingType={listingType}
                      carouselLocation={carouselLocation}
                      headline={headline}
                      fromListingId={fromListingId}
                    />
                  </Slide>
                ))
              : range(4).map(index => (
                  <Slide
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${'section'}-${index}`}
                  >
                    <Box />
                  </Slide>
                ))
          }
        />
      </Box>
    </Box>
  </>
);
export default withRouter(TileCarousel);
